import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ArrowDownToLine, Layers, Sigma } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode, NodeStruct } from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import { nodeLabel } from '@/pages/workflow/helper/repeat-loop';

export const UNION_NODE_ID = 'union_collect_node';
export const REDUCE_NODE_ID = 'reduce_node';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const paramValue = (struct: NodeStruct, fieldId: string): unknown => {
  const param = struct.params?.find(
    (candidate) => candidate.field_id === fieldId,
  );
  return param?.value ?? param?.default;
};

const resultData = (result: unknown): Record<string, unknown> | null => {
  if (!isRecord(result)) return null;
  const data = isRecord(result.data) ? result.data : result;
  return isRecord(data) ? data : null;
};

/** Union: what the settings mean, one line each. */
export const describeUnion = (struct: NodeStruct): string[] => {
  const mode = String(paramValue(struct, 'mode') ?? 'collect');
  const distinct = String(paramValue(struct, 'distinct_mode') ?? 'none');
  const nulls = String(paramValue(struct, 'null_handling') ?? 'include');
  const keyPath = String(paramValue(struct, 'key_path') ?? '');
  const lines = [
    mode === 'union'
      ? 'Union: each incoming value that is a list is spread out one level; other values are kept as single items. [[1,2],[3]] → [1,2,3].'
      : 'Collect: one item per incoming connection, in connection order. [1,2] and 3 → [[1,2],3].',
  ];
  if (distinct === 'canonical_value') {
    lines.push(
      'Duplicates removed: two items are the same when their JSON is the same; the first one wins.',
    );
  } else if (distinct === 'key_path') {
    lines.push(
      `Duplicates removed by key ${keyPath || '(set "Distinct Key Path", e.g. /id)'}: the first item with each key wins.`,
    );
  } else {
    lines.push('Duplicates kept.');
  }
  lines.push(
    nulls === 'drop'
      ? 'Empty (null) items are dropped.'
      : 'Empty (null) items are kept.',
  );
  return lines;
};

const OPERATION_WORDS: Record<string, string> = {
  count: 'count the items',
  sum: 'add the numbers up',
  average: 'average the numbers',
  min: 'take the smallest number',
  max: 'take the largest number',
  any: 'true if any item is true',
  all: 'true if every item is true',
  first_non_null: 'take the first item that is not empty',
  last_non_null: 'take the last item that is not empty',
  concat: 'join the items into one text (or one list)',
  distinct: 'drop duplicate items',
  group_by_key: 'group the items by a key',
};

/** Reduce: what the operation does, in words. */
export const describeReduce = (struct: NodeStruct): string[] => {
  const operation = String(paramValue(struct, 'operation') ?? 'count');
  const keyPath = String(paramValue(struct, 'key_path') ?? '');
  const separator = String(paramValue(struct, 'separator') ?? '');
  const lines = [`Operation: ${OPERATION_WORDS[operation] ?? operation}.`];
  if (operation === 'group_by_key' || (operation === 'distinct' && keyPath)) {
    lines.push(`Key: ${keyPath || '(set "Key Path", e.g. /id)'}.`);
  }
  if (operation === 'concat') {
    lines.push(
      separator
        ? `Separator between items: "${separator}".`
        : 'Items are joined with nothing between them.',
    );
  }
  if (['sum', 'average', 'min', 'max'].includes(operation)) {
    lines.push(
      'Numbers come out as exact decimal text (for example "10"); the editor shows them as numbers.',
    );
  }
  return lines;
};

/** Numeric Reduce results arrive as decimal text; show them as numbers when they parse. */
export const displayReduceValue = (value: unknown): string => {
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    return String(Number(value));
  }
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

interface CollectFoldCardProps {
  nodeId: string;
}

/**
 * Config-panel card for Union / Collect and Reduce: the incoming connections
 * in order with their source names, the settings in words with an example,
 * and after a run the item count (and the folded result for Reduce).
 */
export function CollectFoldCard({ nodeId }: CollectFoldCardProps) {
  const { nodes, edges } = useNodeStore(
    useShallow((state) => ({ nodes: state.nodes, edges: state.edges })),
  );
  const node = nodes.find((candidate: CustomNode) => candidate.id === nodeId);
  const struct = node?.data?.struct;
  const sources = useMemo(
    () =>
      edges
        .filter(
          (edge: CustomEdge) =>
            edge.target === nodeId &&
            edge.edge_type !== ConnectionType.REPEAT &&
            edge.edge_type !== ConnectionType.MAP_COLLECT &&
            edge.edge_type !== ConnectionType.CONDITIONAL,
        )
        .map((edge) => {
          const source = nodes.find(
            (candidate) => candidate.id === edge.source,
          );
          const port = source?.data?.struct?.output_ports?.find(
            (candidate) => candidate.id === edge.sourceHandle,
          );
          return {
            id: edge.id,
            name: nodeLabel(source),
            port: port?.display_name || port?.name || '',
          };
        }),
    [edges, nodes, nodeId],
  );
  if (!struct) return null;
  const isUnion = struct.node_id === UNION_NODE_ID;
  const lines = isUnion ? describeUnion(struct) : describeReduce(struct);
  const data = resultData(node?.data?.result);

  return (
    <section
      className='mx-3 my-2 space-y-2 rounded-md border bg-muted/20 p-2 text-xs'
      data-testid='collect-fold-card'
    >
      <div className='flex items-center gap-1.5 font-semibold'>
        {isUnion ? (
          <Layers className='size-3.5' />
        ) : (
          <Sigma className='size-3.5' />
        )}
        {isUnion ? 'What comes in, in order' : 'What gets folded'}
      </div>
      {sources.length === 0 ? (
        <p className='text-muted-foreground'>
          Nothing connected yet.{' '}
          {isUnion
            ? 'Connect two or more outputs to the Values input.'
            : 'Connect a list to the Items input.'}
        </p>
      ) : (
        <ol className='space-y-0.5' data-testid='collect-fold-sources'>
          {sources.map((source, index) => (
            <li key={source.id} className='flex items-center gap-1.5'>
              <span className='w-4 text-right text-muted-foreground'>
                {index + 1}.
              </span>
              <ArrowDownToLine className='size-3 text-muted-foreground' />
              <span className='font-medium'>{source.name}</span>
              {source.port && (
                <span className='text-muted-foreground'>· {source.port}</span>
              )}
            </li>
          ))}
        </ol>
      )}
      <ul
        className='list-disc space-y-0.5 pl-4 text-[11px] text-muted-foreground'
        data-testid='collect-fold-meaning'
      >
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {data && (
        <div
          className='rounded border bg-background px-2 py-1.5 text-[11px]'
          data-testid='collect-fold-result'
        >
          {isUnion ? (
            <>
              <strong>
                {String(
                  data.output_count ??
                    (Array.isArray(data.items) ? data.items.length : '?'),
                )}
              </strong>{' '}
              items out of {String(data.input_count ?? '?')} in
              {typeof data.duplicates_removed === 'number' &&
              data.duplicates_removed > 0
                ? `; ${data.duplicates_removed} duplicate${data.duplicates_removed === 1 ? '' : 's'} removed`
                : ''}
              .
            </>
          ) : (
            <>
              {OPERATION_WORDS[String(data.operation ?? '')] ??
                String(data.operation ?? 'result')}{' '}
              over {String(data.input_count ?? '?')} items →{' '}
              <strong>{displayReduceValue(data.value)}</strong>
            </>
          )}
        </div>
      )}
    </section>
  );
}
